type SoundName =
  | 'bellows_tap'
  | 'strike_perfect'
  | 'strike_good'
  | 'strike_miss'
  | 'quench_hiss'
  | 'weapon_complete'
  | 'burn_warning';

interface ToneSpec {
  freq: number;
  type: OscillatorType;
  durMs: number;
  attackMs: number;
  releaseMs: number;
  peakGain: number;
  sweepTo?: number;
  noise?: boolean;
}

const TONES: Record<SoundName, ToneSpec> = {
  bellows_tap: { freq: 180, type: 'sine', durMs: 140, attackMs: 10, releaseMs: 120, peakGain: 0.12 },
  strike_perfect: {
    freq: 1400,
    type: 'triangle',
    durMs: 260,
    attackMs: 3,
    releaseMs: 250,
    peakGain: 0.28,
    sweepTo: 600,
  },
  strike_good: {
    freq: 900,
    type: 'triangle',
    durMs: 200,
    attackMs: 5,
    releaseMs: 190,
    peakGain: 0.2,
    sweepTo: 500,
  },
  strike_miss: { freq: 110, type: 'sine', durMs: 180, attackMs: 5, releaseMs: 170, peakGain: 0.18 },
  quench_hiss: {
    freq: 0,
    type: 'sine',
    durMs: 900,
    attackMs: 30,
    releaseMs: 800,
    peakGain: 0.18,
    noise: true,
  },
  weapon_complete: {
    freq: 520,
    type: 'sine',
    durMs: 700,
    attackMs: 10,
    releaseMs: 600,
    peakGain: 0.22,
    sweepTo: 880,
  },
  burn_warning: {
    freq: 300,
    type: 'sawtooth',
    durMs: 140,
    attackMs: 5,
    releaseMs: 130,
    peakGain: 0.16,
    sweepTo: 220,
  },
};

export class AudioManager {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private unlocked = false;
  private noiseBuffer: AudioBuffer | null = null;

  unlock(): void {
    if (this.unlocked) return;
    try {
      const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.7;
      this.master.connect(this.ctx.destination);
      this.unlocked = true;
    } catch {
      this.unlocked = false;
    }
  }

  private ensureNoise(): AudioBuffer | null {
    if (!this.ctx) return null;
    if (this.noiseBuffer) return this.noiseBuffer;
    const len = Math.floor(this.ctx.sampleRate * 1.0);
    const buf = this.ctx.createBuffer(1, len, this.ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    this.noiseBuffer = buf;
    return buf;
  }

  play(name: SoundName): void {
    if (!this.unlocked) this.unlock();
    if (!this.ctx || !this.master) return;
    const spec = TONES[name];
    const now = this.ctx.currentTime;
    const gain = this.ctx.createGain();
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(spec.peakGain, now + spec.attackMs / 1000);
    gain.gain.exponentialRampToValueAtTime(
      0.0001,
      now + spec.attackMs / 1000 + spec.releaseMs / 1000,
    );
    gain.connect(this.master);

    if (spec.noise) {
      const buf = this.ensureNoise();
      if (!buf) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buf;
      const filter = this.ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 3500;
      filter.Q.value = 0.6;
      src.connect(filter);
      filter.connect(gain);
      src.start(now);
      src.stop(now + (spec.durMs + 100) / 1000);
    } else {
      const osc = this.ctx.createOscillator();
      osc.type = spec.type;
      osc.frequency.setValueAtTime(spec.freq, now);
      if (spec.sweepTo !== undefined) {
        osc.frequency.exponentialRampToValueAtTime(
          Math.max(20, spec.sweepTo),
          now + spec.durMs / 1000,
        );
      }
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + (spec.durMs + 100) / 1000);
    }
  }
}
